import { Card, CardContent } from "@components/common/card"
import { cn } from "@pma/utils/helpers"
import { TrendingUp, TrendingDown } from "lucide-react"

// ---------------------------------------------------------------------------
// Color maps
// ---------------------------------------------------------------------------

const gradientMap: Record<string, string> = {
  blue: "from-blue-50 to-white",
  amber: "from-amber-50 to-white",
  green: "from-green-50 to-white",
  red: "from-red-50 to-white",
  purple: "from-purple-50 to-white",
  indigo: "from-indigo-50 to-white",
}

const iconBgMap: Record<string, string> = {
  blue: "bg-blue-100 text-blue-600",
  amber: "bg-amber-100 text-amber-600",
  green: "bg-green-100 text-green-600",
  red: "bg-red-100 text-red-600",
  purple: "bg-purple-100 text-purple-600",
  indigo: "bg-indigo-100 text-indigo-600",
}

const valueColorMap: Record<string, string> = {
  blue: "text-blue-700",
  amber: "text-amber-700",
  green: "text-green-700",
  red: "text-red-700",
  purple: "text-purple-700",
  indigo: "text-indigo-700",
}

const borderColorMap: Record<string, string> = {
  blue: "border-blue-100",
  amber: "border-amber-100",
  green: "border-green-100",
  red: "border-red-100",
  purple: "border-purple-100",
  indigo: "border-indigo-100",
}

// ---------------------------------------------------------------------------
// KpiCard
// ---------------------------------------------------------------------------

export interface KpiCardProps {
  label: string
  value: number | string
  icon: React.ElementType
  color: "blue" | "amber" | "green" | "red" | "purple" | "indigo"
  trend?: number | null
  subtitle?: string
  onClick?: () => void
  /** Override the default gradient class (e.g. "from-emerald-50 via-emerald-50/60 to-white") */
  gradient?: string
  /** Override the icon background class */
  iconBg?: string
  /** Override the icon color class */
  iconColor?: string
  /** Override the value text color class */
  valueColor?: string
}

export function KpiCard({ label, value, icon: Icon, color, trend, subtitle, onClick, gradient, iconBg, iconColor, valueColor }: KpiCardProps) {
  const hasTrend = trend !== null && trend !== undefined
  const isUp = hasTrend && trend! > 0
  const isDown = hasTrend && trend! < 0

  return (
    <Card
      className={cn(
        "relative overflow-hidden bg-gradient-to-br border shadow-sm hover:shadow-md transition-shadow duration-200",
        gradient || gradientMap[color],
        !gradient && borderColorMap[color],
        onClick && "cursor-pointer",
      )}
      onClick={onClick}
    >
      <CardContent className="flex flex-col gap-2 p-4">
        <div className="flex items-center justify-between">
          <div
            className={cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
              iconBg || iconBgMap[color],
            )}
          >
            <Icon className={cn("h-4 w-4", iconColor)} />
          </div>
          {hasTrend && (
            <span
              className={cn(
                "flex items-center gap-0.5 text-xs font-medium rounded-full px-1.5 py-0.5",
                isUp && "text-green-700 bg-green-50",
                isDown && "text-red-600 bg-red-50",
                !isUp && !isDown && "text-gray-500 bg-gray-50",
              )}
              aria-label={`Trend: ${trend! > 0 ? "up" : "down"} ${Math.abs(trend!)}%`}
            >
              {isUp ? <TrendingUp className="h-3 w-3" /> : isDown ? <TrendingDown className="h-3 w-3" /> : null}
              {trend! > 0 ? "+" : ""}{trend!}%
            </span>
          )}
        </div>
        <div>
          <p className={cn("font-mono text-3xl font-bold tabular-nums leading-none", valueColor || valueColorMap[color])}>
            {typeof value === "number" ? value.toLocaleString() : value}
          </p>
          <p className="text-xs font-medium text-muted-foreground mt-1 leading-tight">{label}</p>
          {subtitle && (
            <p className="text-[10px] text-muted-foreground/70 mt-0.5 leading-tight">{subtitle}</p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// SkeletonKpiCard
// ---------------------------------------------------------------------------

export function SkeletonKpiCard() {
  return (
    <Card className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white border border-gray-100 shadow-sm animate-pulse">
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex items-center justify-between">
          <div className="h-9 w-9 rounded-lg bg-gray-200" />
          <div className="h-5 w-12 rounded-full bg-gray-200" />
        </div>
        <div className="space-y-1.5">
          <div className="h-8 w-16 rounded bg-gray-200" />
          <div className="h-3 w-24 rounded bg-gray-200" />
        </div>
      </CardContent>
    </Card>
  )
}
